import { supabase } from '../lib/supabase';

/**
 * Payroll Service - Handles all payroll and salary operations
 * Database schema uses snake_case, React uses camelCase
 */

const payrollService = {
  /**
   * Fetch all employees with their salary information
   * Joins employees with user_profiles to get complete employee data
   */
  async getAllEmployeesWithSalary() {
    const { data, error } = await supabase?.from('employees')?.select(`
        id,
        employee_id,
        position,
        department,
        salary,
        employment_status,
        date_of_joining,
        user_profiles!employees_user_id_fkey (
          full_name,
          email,
          phone,
          pf_number,
          esi_number,
          bank_name,
          account_number,
          ifsc_code
        )
      `)?.eq('employment_status', 'active')?.order('employee_id');

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(emp => ({
      id: emp?.id,
      employeeId: emp?.employee_id,
      fullName: emp?.user_profiles?.full_name || 'Unknown',
      email: emp?.user_profiles?.email || '',
      phone: emp?.user_profiles?.phone || '',
      position: emp?.position,
      department: emp?.department,
      baseSalary: emp?.salary || 0,
      employmentStatus: emp?.employment_status,
      dateOfJoining: emp?.date_of_joining,
      pfNumber: emp?.user_profiles?.pf_number || '',
      esiNumber: emp?.user_profiles?.esi_number || '',
      bankName: emp?.user_profiles?.bank_name || '',
      accountNumber: emp?.user_profiles?.account_number || '',
      ifscCode: emp?.user_profiles?.ifsc_code || ''
    }));
  },

  /**
   * Get salary records for a specific pay period
   */
  async getSalaryRecordsByPeriod(startDate, endDate) {
    const { data, error } = await supabase?.from('salary_records')?.select(`
        *,
        employees!salary_records_employee_id_fkey (
          employee_id,
          position,
          department,
          user_profiles!employees_user_id_fkey (
            full_name,
            email
          )
        )
      `)?.gte('pay_period_start', startDate)?.lte('pay_period_end', endDate)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(record => ({
      id: record?.id,
      employeeId: record?.employees?.employee_id || '',
      fullName: record?.employees?.user_profiles?.full_name || 'Unknown',
      email: record?.employees?.user_profiles?.email || '',
      position: record?.employees?.position || '',
      department: record?.employees?.department || '',
      baseSalary: parseFloat(record?.base_salary) || 0,
      allowances: parseFloat(record?.allowances) || 0,
      bonuses: parseFloat(record?.bonuses) || 0,
      incentives: parseFloat(record?.incentives) || 0,
      overtimePay: parseFloat(record?.overtime_pay) || 0,
      grossPay: parseFloat(record?.gross_pay) || 0,
      deductions: parseFloat(record?.deductions) || 0,
      taxDeductions: parseFloat(record?.tax_deductions) || 0,
      netPay: parseFloat(record?.net_pay) || 0,
      payrollStatus: record?.payroll_status,
      payPeriodStart: record?.pay_period_start,
      payPeriodEnd: record?.pay_period_end,
      processedAt: record?.processed_at
    }));
  },

  /**
   * Get salary record for specific employee and period
   */
  async getEmployeeSalarySlip(employeeId, payPeriodStart, payPeriodEnd) {
    const { data, error } = await supabase?.from('salary_records')?.select(`
        *,
        employees!salary_records_employee_id_fkey (
          employee_id,
          position,
          department,
          date_of_joining,
          user_profiles!employees_user_id_fkey (
            full_name,
            email,
            phone,
            pf_number,
            esi_number,
            bank_name,
            account_number,
            ifsc_code
          )
        )
      `)?.eq('employee_id', employeeId)?.eq('pay_period_start', payPeriodStart)?.eq('pay_period_end', payPeriodEnd)?.single();

    if (error) {
      if (error?.code === 'PGRST116') {
        return null; // No record found
      }
      throw error;
    }

    return {
      id: data?.id,
      employeeId: data?.employees?.employee_id || '',
      fullName: data?.employees?.user_profiles?.full_name || '',
      email: data?.employees?.user_profiles?.email || '',
      phone: data?.employees?.user_profiles?.phone || '',
      position: data?.employees?.position || '',
      department: data?.employees?.department || '',
      dateOfJoining: data?.employees?.date_of_joining || '',
      pfNumber: data?.employees?.user_profiles?.pf_number || '',
      esiNumber: data?.employees?.user_profiles?.esi_number || '',
      bankName: data?.employees?.user_profiles?.bank_name || '',
      accountNumber: data?.employees?.user_profiles?.account_number || '',
      ifscCode: data?.employees?.user_profiles?.ifsc_code || '',
      baseSalary: parseFloat(data?.base_salary) || 0,
      allowances: parseFloat(data?.allowances) || 0,
      bonuses: parseFloat(data?.bonuses) || 0,
      incentives: parseFloat(data?.incentives) || 0,
      overtimePay: parseFloat(data?.overtime_pay) || 0,
      overtimeHours: parseFloat(data?.overtime_hours) || 0,
      grossPay: parseFloat(data?.gross_pay) || 0,
      deductions: parseFloat(data?.deductions) || 0,
      taxDeductions: parseFloat(data?.tax_deductions) || 0,
      netPay: parseFloat(data?.net_pay) || 0,
      payrollStatus: data?.payroll_status,
      payPeriodStart: data?.pay_period_start,
      payPeriodEnd: data?.pay_period_end,
      payrollNotes: data?.payroll_notes || '',
      processedAt: data?.processed_at
    };
  },

  /**
   * Process batch payroll for selected employees
   * Uses the existing process_batch_payroll function
   */
  async processBatchPayroll(employeeIds, payPeriodStart, payPeriodEnd) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.rpc('process_batch_payroll', {
      p_employee_ids: employeeIds,
      p_pay_period_start: payPeriodStart,
      p_pay_period_end: payPeriodEnd,
      p_processed_by: user?.id
    });

    if (error) throw error;

    return data?.map(result => ({
      employeeId: result?.employee_id,
      employeeName: result?.employee_name,
      baseSalary: parseFloat(result?.base_salary) || 0,
      netPay: parseFloat(result?.net_pay) || 0,
      status: result?.status,
      errorMessage: result?.error_message
    }));
  },

  /**
   * Create/Update salary record for an employee
   */
  async createSalaryRecord(salaryData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert camelCase to snake_case for database
    const dbData = {
      employee_id: salaryData?.employeeId,
      base_salary: salaryData?.baseSalary,
      allowances: salaryData?.allowances || 0,
      bonuses: salaryData?.bonuses || 0,
      incentives: salaryData?.incentives || 0,
      overtime_hours: salaryData?.overtimeHours || 0,
      overtime_pay: salaryData?.overtimePay || 0,
      deductions: salaryData?.deductions || 0,
      tax_deductions: salaryData?.taxDeductions || 0,
      pay_period_start: salaryData?.payPeriodStart,
      pay_period_end: salaryData?.payPeriodEnd,
      payroll_notes: salaryData?.payrollNotes || '',
      processed_by: user?.id
    };

    const { data, error } = await supabase?.from('salary_records')?.insert(dbData)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      grossPay: parseFloat(data?.gross_pay) || 0,
      netPay: parseFloat(data?.net_pay) || 0
    };
  },

  /**
   * Get payroll summary statistics
   */
  async getPayrollSummary(startDate, endDate, department = null) {
    const { data, error } = await supabase?.rpc('get_payroll_summary', {
      p_start_date: startDate,
      p_end_date: endDate,
      p_department: department
    });

    if (error) throw error;

    const summary = data?.[0] || {};
    return {
      periodStart: summary?.period_start,
      periodEnd: summary?.period_end,
      totalEmployees: parseInt(summary?.total_employees) || 0,
      totalGrossPay: parseFloat(summary?.total_gross_pay) || 0,
      totalNetPay: parseFloat(summary?.total_net_pay) || 0,
      totalBonuses: parseFloat(summary?.total_bonuses) || 0,
      totalIncentives: parseFloat(summary?.total_incentives) || 0,
      totalDeductions: parseFloat(summary?.total_deductions) || 0,
      averageSalary: parseFloat(summary?.average_salary) || 0
    };
  }
};

export default payrollService;